/**
 * Workspace variables (YAG-831), at the top of `/services/repositories`: a
 * value set once that every repository's builds and environment runs read.
 * A repository's own value of the same name wins.
 *
 * Values are write-only. The list shows each name and when it was set; a
 * value typed or pasted here is sent and never shown again.
 */
import { useState } from "react";
import type { WorkspaceVariable } from "@yagni/shared";
import { Button, Input } from "../ui/index.js";
import { SettingsRow } from "../workers/SettingsRow.js";
import { formatRelative } from "../lib/relativeTime.js";
import { EnvPaste } from "./EnvironmentConfigRows.js";
import { Row } from "./environmentControls.js";
import { GithubPackagesTokenHelp, isGithubPackagesToken } from "./GithubPackagesTokenHelp.js";
import { count } from "./EnvironmentConfigSummary.js";
import { isVariableName } from "./environmentPage.js";
import {
  WORKSPACE_VARIABLES_FAILED,
  removeWorkspaceVariable,
  setWorkspaceVariables,
} from "./workspaceVariablesApi.js";
import "./repositoryEnvironment.css";

export const WORKSPACE_VARIABLES_HELP =
  "Every repository in this workspace sees these. A repository's own value wins.";

export function workspaceVariablesSummary(
  variables: readonly WorkspaceVariable[],
  loaded: boolean,
): string {
  if (!loaded) return "Reading";
  return variables.length ? `${count(variables.length, "variable")} set` : "None yet";
}

/** "Set 3d ago", "Set just now". */
export function setWhen(setAt: string, now: number = Date.now()): string {
  return `Set ${formatRelative(setAt, now)}`;
}

export function WorkspaceVariables({
  variables,
  loaded,
  error: readError,
  reload,
  open,
  onToggle,
  githubPackagesNeeded = false,
}: {
  variables: WorkspaceVariable[];
  loaded: boolean;
  error: string | null;
  reload: () => Promise<void>;
  open: boolean;
  onToggle: () => void;
  /** A repository here reads GitHub Packages: say what its token must be, set or not. */
  githubPackagesNeeded?: boolean;
}) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(work: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await work();
      await reload();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : WORKSPACE_VARIABLES_FAILED);
      return false;
    } finally {
      setBusy(false);
    }
  }

  const shownError = error ?? readError;
  const ready = isVariableName(name) && value.length > 0;

  return (
    <SettingsRow
      section="workspace-variables"
      testId="workspace-variables"
      label="Workspace variables"
      help={WORKSPACE_VARIABLES_HELP}
      summary={workspaceVariablesSummary(variables, loaded)}
      open={open}
      onToggle={onToggle}
    >
      <Row title="Shared by every repository" help={WORKSPACE_VARIABLES_HELP} />
      {shownError && (
        <p role="alert" className="qa-notice">
          {shownError}
        </p>
      )}
      {githubPackagesNeeded &&
        !variables.some((variable) => isGithubPackagesToken(variable.name)) && (
          <Row
            title="GITHUB_PACKAGES_TOKEN is not set"
            help={
              <>
                A repository here installs from GitHub Packages. Add it below.{" "}
                <GithubPackagesTokenHelp />
              </>
            }
            testId="workspace-variable-github-packages-missing"
          />
        )}
      {variables.map((variable) => (
        <Row
          key={variable.name}
          title={variable.name}
          help={
            isGithubPackagesToken(variable.name) ? (
              <>
                {setWhen(variable.setAt)}. <GithubPackagesTokenHelp />
              </>
            ) : (
              setWhen(variable.setAt)
            )
          }
          testId={`workspace-variable-${variable.name}`}
        >
          <span className="wrk-srow-controls">
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => void act(() => removeWorkspaceVariable(variable.name))}
            >
              {`Remove ${variable.name}`}
            </Button>
          </span>
        </Row>
      ))}
      <EnvPaste
        title="Paste a .env for the workspace"
        testId="workspace-env-paste"
        help="Each NAME=value line becomes a workspace variable. Values are write-only."
        actionLabel="Save variables"
        doneVerb="Saved"
        nothingLine="Nothing was saved."
        onStage={(values) => void act(() => setWorkspaceVariables(values))}
      />
      <Row title="Add a variable" help="Values are write-only. Only the name is ever shown again.">
        {/* Name, value and Set stack in a column: side by side they overflow
            the sheet's narrow column and push Set out of reach. */}
        <span className="wrk-srow-controls qa-service qa-add-variable">
          <label>
            Workspace variable name
            <Input
              value={name}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              onChange={(event) => setName(event.target.value.trim())}
            />
          </label>
          <label>
            Workspace variable value
            <Input
              type="password"
              value={value}
              autoComplete="new-password"
              disabled={busy}
              onChange={(event) => setValue(event.target.value)}
            />
          </label>
          <Button
            variant="secondary"
            disabled={busy || !ready}
            onClick={() =>
              void act(() => setWorkspaceVariables({ [name]: value })).then((ok) => {
                if (!ok) return;
                setName("");
                setValue("");
              })
            }
          >
            Set variable
          </Button>
        </span>
      </Row>
    </SettingsRow>
  );
}
